import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ChevronRight, Clock, Flame, Zap } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import useCountdown from '../hooks/useCountdown'
import { formatClockTimer } from '../utils/rushdropFormat'
import { hapticImpact } from '../../../utils/haptic'

// Интервал обновления me.fast_event для карточки на главной
const TEASER_POLL_INTERVAL = 15000

/**
 * Карточка-тизер Rush Drop на главной странице.
 * Данные — компактный блок me.fast_event из /api/me/ (не тянем полный ответ эвента).
 * Карточка появляется и исчезает сама: компонент периодически обновляет профиль,
 * а при fast_event = null ничего не рендерит.
 */
const RushDropTeaser = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const fastEvent = useAppStore((state) => state.user?.fast_event)
  const loadProfile = useAppStore((state) => state.loadProfile)
  const { playSound } = useAudioManager({ select: 'select' })

  // Периодическое обновление me.fast_event — эвент возникает внезапно,
  // без действий пользователя, поэтому карточка должна появляться сама.
  // Запрашиваем сразу при монтировании (переход на главную), иначе до первого
  // тика поллинга отображаются устаревшие статус и таймер.
  useEffect(() => {
    loadProfile().catch(() => {})

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadProfile().catch(() => {})
      }
    }, TEASER_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [loadProfile])

  const remaining = useCountdown(fastEvent?.seconds)

  if (!fastEvent) return null

  const { state, title, image, joined, tasks_done: tasksDone, tasks_total: tasksTotal } = fastEvent
  const hasImage = Boolean(image)
  const isEnding = state === 'ending'

  const handleOpen = () => {
    playSound('select')
    hapticImpact('light')
    navigate('/rushdrop')
  }

  // Финал эвента — компактная плашка «Смотреть итоги», основной ивент уже завершён
  if (state === 'finished') {
    return (
      <motion.button
        type="button"
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35 }}
        onClick={handleOpen}
        className="relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-accent-primary/30 bg-surface p-3 text-left transition-all active:scale-[0.98]"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-primary/20">
          <Zap size={20} className="text-accent-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-accent-primary">
            Rush Drop
          </p>
          <p className="truncate text-sm font-semibold text-white">{title}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-accent-primary">
          {t('rushdrop.teaser.seeResults', 'Смотреть итоги')}
          <ChevronRight size={16} />
        </span>
      </motion.button>
    )
  }

  // Большой баннер: soon / active / ending
  const progressPercent = tasksTotal > 0
    ? Math.min(100, Math.round((tasksDone / tasksTotal) * 100))
    : 0

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35 }}
      onClick={handleOpen}
      className="relative aspect-square w-full overflow-hidden rounded-2xl border border-accent-primary/30 bg-surface text-left transition-all active:scale-[0.99] lg:aspect-[4/3]"
    >
      {/* Фон: картинка эвента или градиентная плашка по умолчанию */}
      {hasImage ? (
        <img
          src={image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-accent-primary/25 via-surface to-accent-secondary/20">
          <Zap size={72} className="text-accent-primary/30" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20" />

      {/* Верх: бейджи + таймер */}
      <div className="absolute left-3 right-3 top-3 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-md bg-accent-primary px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-dark-primary">
            Rush Drop
          </span>
          {isEnding && (
            <span className="flex items-center gap-1 rounded-md bg-red-500/90 px-2 py-0.5 text-[11px] font-bold text-white">
              <Flame size={12} />
              {t('rushdrop.endingSoon', 'Финал скоро')}
            </span>
          )}
        </div>
        <span
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 backdrop-blur-md ${
            isEnding
              ? 'animate-pulse-glow-red border-red-500/40 bg-red-500/15 text-red-300'
              : 'border-white/15 bg-black/50 text-white'
          }`}
        >
          <Clock size={13} />
          <span className="text-sm font-bold tabular-nums">{formatClockTimer(remaining)}</span>
        </span>
      </div>

      {/* Низ: название, статус, CTA */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-xl font-black leading-tight text-white">{title}</h3>

        {(joined || state === 'soon' || Number(tasksDone) > 0) && (
          <div className="mt-3">
            {joined ? (
              <span className="inline-flex rounded-md bg-accent-primary px-2.5 py-1 text-xs font-bold text-dark-primary">
                {t('rushdrop.status.inGame', 'Ты в игре')}
              </span>
            ) : state === 'soon' ? (
              <p className="text-xs text-text-secondary">
                {t('rushdrop.teaser.soonHint', 'Выполни задания после старта — и попадёшь в розыгрыш призов')}
              </p>
            ) : (
              <div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">{t('rushdrop.tasks.title', 'Задания')}</span>
                  <span className="font-semibold tabular-nums text-white">
                    {tasksDone} / {tasksTotal}
                  </span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-700"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-1 text-sm font-semibold text-accent-primary">
          {t('rushdrop.teaser.open', 'Открыть эвент')}
          <ChevronRight size={16} />
        </div>
      </div>
    </motion.button>
  )
}

export default RushDropTeaser
